import { Button } from '@xcentral/ui';
import { type Notification } from './useNotifications';

interface NotificationPanelProps {
  open: boolean;
  onClose: () => void;
  notifications: Notification[];
  onMarkRead: (id: string) => void;
  onMarkAllRead: () => void;
  onDismiss: (id: string) => void;
}

const priorityIcon: Record<string, string> = {
  URGENT: '🔴',
  HIGH: '🟠',
  NORMAL: '🔵',
  LOW: '⚪',
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

export function NotificationPanel({ open, onClose, notifications, onMarkRead, onMarkAllRead, onDismiss }: NotificationPanelProps) {
  if (!open) return null;

  const unread = notifications.filter(n => n.status === 'SENT');

  return (
    <>
      {/* Backdrop — button so screen readers + keyboard users can dismiss the panel */}
      <button
        type="button"
        className="fixed inset-0 z-40"
        onClick={onClose}
        aria-label="Close notifications panel"
      />

      {/* Panel */}
      <div className="fixed right-0 top-16 z-50 w-96 max-h-[calc(100vh-4rem)] bg-white border-l border-gray-200 shadow-lg flex flex-col animate-in slide-in-from-right duration-200">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
          <div className="flex items-center gap-2">
            {unread.length > 0 && (
              <Button variant="ghost" size="sm" onClick={onMarkAllRead}>Mark all read</Button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600" aria-label="Close">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Notification list */}
        <div className="flex-1 overflow-y-auto">
          {notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-400">
              <svg className="w-12 h-12 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
              </svg>
              <p className="text-sm">No notifications</p>
            </div>
          ) : (
            notifications.map(n => (
              <div
                key={n.id}
                role="button"
                tabIndex={0}
                aria-label={n.status === 'SENT' ? `Mark "${n.subject}" as read` : n.subject}
                className={`flex gap-3 p-4 border-b border-gray-100 hover:bg-gray-50 cursor-pointer transition-colors
                  ${n.status === 'SENT' ? 'bg-indigo-50/50' : ''}`}
                onClick={() => { if (n.status === 'SENT') onMarkRead(n.id); }}
                onKeyDown={(e) => {
                  if (n.status === 'SENT' && (e.key === 'Enter' || e.key === ' ')) {
                    e.preventDefault();
                    onMarkRead(n.id);
                  }
                }}
              >
                <span className="text-base mt-0.5">{priorityIcon[n.priority] || '🔵'}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className={`text-sm truncate ${n.status === 'SENT' ? 'font-semibold text-gray-900' : 'text-gray-700'}`}>
                      {n.subject}
                    </p>
                    {n.status === 'SENT' && <span className="w-2 h-2 rounded-full bg-indigo-500 flex-shrink-0" />}
                  </div>
                  <p className="text-xs text-gray-500 mt-0.5 line-clamp-2">{n.body}</p>
                  <p className="text-xs text-gray-400 mt-1">{timeAgo(n.createdAt)}</p>
                </div>
                <button
                  onClick={e => { e.stopPropagation(); onDismiss(n.id); }}
                  className="text-gray-300 hover:text-gray-500 flex-shrink-0"
                  aria-label="Dismiss"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-gray-200 text-center">
          <a href="/notification-preferences" className="text-sm text-indigo-600 hover:text-indigo-800">
            Notification Preferences
          </a>
        </div>
      </div>
    </>
  );
}
